import { useState, useEffect } from 'react'
import { format, eachDayOfInterval, parseISO } from 'date-fns'
import { Plus, ChevronDown, ChevronRight, Pencil, Trash2, Users, FileText, Calendar as CalendarIcon } from 'lucide-react'
import type { Tour, Activity } from '@/types'
import { useActivitiesByTour, useActivitiesRealtime, useDeleteActivity, useTourAssignments } from '@/hooks/useActivities'
import { formatInTimezone, getTimezoneAbbr, getLocalDate, formatDayHeader } from '@/lib/timezones'
import { ActivityFormDialog } from './ActivityFormDialog'
import { DaySheetDialog } from '@/components/DaySheetDialog'
import { useAuth } from '@/context/AuthContext'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { useNow } from '@/hooks/useNow'
import { notifyActivityChange } from '@/lib/pushNotifications'
import { titleWithEmoji } from '@/lib/activityEmoji'
import { getActivityStatusMap, type ActivityStatus } from '@/lib/activityStatus'
import { getTourStatus } from '@/components/TourStatus'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'

interface Props {
  tour: Tour | null
  loading: boolean
}

// Inicialización del día seleccionado (desktop split-view): hoy si cae dentro
// del tour, si no el primer día. Se evalúa una sola vez al montar el tour —
// si el user cambia de tour, el useEffect re-sincroniza.
function initialSelectedDay(tour: Tour): string {
  const today = format(new Date(), 'yyyy-MM-dd')
  if (today >= tour.fecha_inicio && today <= tour.fecha_fin) return today
  return tour.fecha_inicio
}

export function ScheduleView({ tour, loading }: Props) {
  const [formOpen, setFormOpen] = useState(false)
  const [editingActivity, setEditingActivity] = useState<Activity | null>(null)
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set())
  const [selectedDay, setSelectedDay] = useState<string | null>(null)
  // Snapshot de las actividades del día cuando el sheet dialog abre.
  const [sheetDay, setSheetDay] = useState<{ day: string; acts: Activity[] } | null>(null)

  const isDesktop = useMediaQuery('(min-width: 1024px)')
  const now = useNow(60_000)

  const { user } = useAuth()
  const { data: activities = [], isLoading: activitiesLoading } = useActivitiesByTour(tour?.id)
  const { data: assignmentsByActivity = {} } = useTourAssignments(tour?.id)
  const { subscribe } = useActivitiesRealtime(tour?.id)
  const deleteActivity = useDeleteActivity()

  const statusMap = getActivityStatusMap(activities, now)

  useEffect(() => {
    const unsubscribe = subscribe()
    return () => { unsubscribe?.() }
  }, [tour?.id])

  // Mobile: expandir hoy por default.
  useEffect(() => {
    const today = format(new Date(), 'yyyy-MM-dd')
    setExpandedDays(new Set([today]))
  }, [])

  // Desktop: sincronizar selectedDay con el tour activo.
  useEffect(() => {
    if (tour) setSelectedDay(initialSelectedDay(tour))
    else setSelectedDay(null)
  }, [tour?.id])

  // Keyboard shortcut "N" → Add activity (desktop split-view only). Ignorado
  // cuando hay un input/textarea con foco o cuando el form/day-sheet está
  // abierto, así no pisa lo que el user está tipeando. DEBE vivir arriba de
  // los early returns para respetar la Rules of Hooks.
  useEffect(() => {
    if (!isDesktop) return
    const handler = (e: KeyboardEvent) => {
      if (formOpen || sheetDay) return
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return
      const active = document.activeElement as HTMLElement | null
      if (active && (
        active.tagName === 'INPUT' ||
        active.tagName === 'TEXTAREA' ||
        active.tagName === 'SELECT' ||
        active.isContentEditable
      )) return
      if (e.key === 'n' || e.key === 'N') {
        e.preventDefault()
        setEditingActivity(null)
        setFormOpen(true)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isDesktop, formOpen, sheetDay])

  if (loading || activitiesLoading) {
    return (
      <div className="space-y-3 max-w-2xl mx-auto lg:max-w-none">
        {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 rounded-xl" />)}
      </div>
    )
  }

  if (!tour) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center max-w-2xl mx-auto">
        <span className="text-4xl mb-3">🗓️</span>
        <h2 className="font-medium text-foreground mb-1">No active tour</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Create a tour in the Tours tab to get started.
        </p>
      </div>
    )
  }

  const groupedByDay = activities.reduce<Record<string, Activity[]>>((acc, activity) => {
    const localDate = getLocalDate(activity.start_time_utc, activity.venue_timezone)
    if (!acc[localDate]) acc[localDate] = []
    acc[localDate].push(activity)
    return acc
  }, {})

  const tourDays = eachDayOfInterval({
    start: parseISO(tour.fecha_inicio),
    end: parseISO(tour.fecha_fin),
  }).map(d => format(d, 'yyyy-MM-dd'))

  const status = getTourStatus(tour)

  function toggleDay(dateStr: string) {
    setExpandedDays(prev => {
      const next = new Set(prev)
      if (next.has(dateStr)) next.delete(dateStr)
      else next.add(dateStr)
      return next
    })
  }

  async function handleDelete(activity: Activity) {
    if (!confirm(`Delete "${activity.titulo}"?`)) return
    try {
      await deleteActivity.mutateAsync({ id: activity.id, tourId: activity.tour_id })
      toast.success('Activity deleted')
      if (tour) {
        notifyActivityChange({
          type: 'deleted',
          activityId: activity.id,
          activityTitle: activity.titulo,
          tourId: tour.id,
          orgId: tour.org_id,
          visibility: activity.visibility_level,
          changedBy: user?.id ?? '',
        })
      }
    } catch {
      toast.error('Failed to delete activity')
    }
  }

  function openNewActivity() {
    setEditingActivity(null)
    setFormOpen(true)
  }

  function openEditActivity(a: Activity) {
    setEditingActivity(a)
    setFormOpen(true)
  }

  // Progress bar compartida entre mobile y desktop
  const ProgressBar = status.phase !== 'upcoming' ? (
    <div className="tour-progress">
      <span className="tp-label tp-label-eyebrow">
        {status.phase === 'completed'
          ? 'Tour completed'
          : (
            <>
              <span>Day</span>
              <span className="mono">{status.dayNumber}</span>
              <span className="text-[color:var(--subtle-foreground)]">/</span>
              <span className="mono">{status.totalDays}</span>
            </>
          )}
      </span>
      <div className="tp-bar">
        <div
          className="tp-fill"
          style={{ '--fill': status.pct / 100 } as React.CSSProperties}
        />
      </div>
      <span className="tp-pct mono">{status.pct}%</span>
    </div>
  ) : null

  // ── Desktop: split view (~340px day list + flex-1 activities panel) ──
  if (isDesktop) {
    const selectedActivities = (selectedDay ? groupedByDay[selectedDay] ?? [] : [])
      .slice()
      .sort((a, b) => a.start_time_utc.localeCompare(b.start_time_utc))

    return (
      <>
        {ProgressBar}
        <div className="grid grid-cols-[340px_1fr] gap-6 mt-2">
          {/* Day list — sticky rail. Sin label arriba: la rail se alinea
              con el top del panel derecho (ambos arrancan en la misma y). */}
          <aside className="min-w-0">
            <div className="sticky top-[calc(env(safe-area-inset-top,0px)+76px)]">
              <ul className="rounded-2xl border border-border bg-card overflow-hidden divide-y divide-border max-h-[calc(100vh-180px)] overflow-y-auto">
                {tourDays.map((dateStr, i) => {
                  const d = parseISO(dateStr)
                  const count = (groupedByDay[dateStr] ?? []).length
                  const isToday = dateStr === format(new Date(), 'yyyy-MM-dd')
                  const isSelected = dateStr === selectedDay
                  return (
                    <li key={dateStr}>
                      <button
                        type="button"
                        onClick={() => setSelectedDay(dateStr)}
                        style={{ animationDelay: `${i * 25}ms` }}
                        className={cn(
                          'relative w-full flex items-center gap-3.5 pl-5 pr-3 py-3 text-left transition-colors dur-fast group',
                          'animate-in fade-in slide-in-from-left-2 fill-mode-backwards dur-base ease-out-soft',
                          isSelected ? 'bg-card-2' : 'hover:bg-card-2/60'
                        )}
                      >
                        {/* Active bar — thin primary, morph in */}
                        <span
                          className={cn(
                            'absolute left-0 top-1/2 -translate-y-1/2 w-0.5 rounded-r-full transition-all dur-base ease-out-soft',
                            isSelected ? 'h-9 bg-primary' : 'h-0 bg-transparent'
                          )}
                          aria-hidden
                        />

                        {/* Editorial date block: weekday abbr + big day number */}
                        <div className="flex flex-col items-start leading-none shrink-0 w-9">
                          <span className={cn(
                            'mono text-[9px] font-semibold uppercase tracking-[0.14em] mb-1 transition-colors',
                            isSelected ? 'text-primary' : 'text-muted-foreground'
                          )}>
                            {format(d, 'EEE')}
                          </span>
                          <span className={cn(
                            'text-[20px] font-semibold tabular-nums tracking-tight leading-none transition-colors',
                            isSelected ? 'text-foreground' : 'text-foreground/85'
                          )}>
                            {format(d, 'd')}
                          </span>
                        </div>

                        {/* Meta: month + today chip + count */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-[12.5px] font-medium text-foreground/90 truncate">
                              {format(d, 'MMMM')}
                            </span>
                            {isToday && (
                              <span className="text-[9px] font-semibold uppercase tracking-wider text-primary">
                                Today
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-muted-foreground mt-0.5">
                            {count === 0
                              ? 'Empty'
                              : `${count} ${count === 1 ? 'activity' : 'activities'}`}
                          </p>
                        </div>

                        {/* Chevron — visible always for selected, hover-reveal otherwise */}
                        <ChevronRight
                          className={cn(
                            'w-3.5 h-3.5 shrink-0 transition-all dur-fast',
                            isSelected
                              ? 'text-primary opacity-70'
                              : 'text-muted-foreground opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0'
                          )}
                          strokeWidth={2.5}
                        />
                      </button>
                    </li>
                  )
                })}
              </ul>
            </div>
          </aside>

          {/* Right panel — day title + actions + activities */}
          <section className="min-w-0">
            {selectedDay && (
              <DayPanel
                key={selectedDay}
                tour={tour}
                day={selectedDay}
                activities={selectedActivities}
                assignmentsByActivity={assignmentsByActivity}
                statusMap={statusMap}
                onAdd={openNewActivity}
                onDaySheet={() => setSheetDay({ day: selectedDay, acts: selectedActivities })}
                onEdit={openEditActivity}
                onDelete={handleDelete}
              />
            )}
          </section>
        </div>

        <ActivityFormDialog
          open={formOpen}
          onOpenChange={setFormOpen}
          tour={tour}
          editingActivity={editingActivity}
          defaultDate={selectedDay ?? undefined}
        />

        {sheetDay && (
          <DaySheetDialog
            tour={tour}
            day={sheetDay.day}
            activities={sheetDay.acts}
            open
            onOpenChange={(o) => { if (!o) setSheetDay(null) }}
          />
        )}
      </>
    )
  }

  // ── Mobile / tablet: day accordion + FAB dock (unchanged) ──
  return (
    <div className="max-w-2xl mx-auto">
      {ProgressBar}

      <div className="space-y-3">
        {tourDays.map((dateStr, dayIndex) => {
          const dayActivities = (groupedByDay[dateStr] ?? []).sort((a, b) =>
            a.start_time_utc.localeCompare(b.start_time_utc)
          )
          const isExpanded = expandedDays.has(dateStr)
          const isToday = dateStr === format(new Date(), 'yyyy-MM-dd')

          return (
            <div
              key={dateStr}
              style={{ animationDelay: `${dayIndex * 40}ms` }}
              className="rounded-xl border border-border bg-card overflow-hidden animate-in fade-in slide-in-from-top-2 fill-mode-backwards dur-base ease-out-soft"
            >
              <div className="flex items-stretch">
                <button
                  className="flex-1 flex items-center justify-between px-4 py-3.5 hover:bg-card-2 transition-colors dur-fast text-left min-w-0"
                  onClick={() => toggleDay(dateStr)}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[14px] font-semibold text-foreground">
                        {formatDayHeader(dateStr)}
                      </span>
                      {isToday && (
                        <span className="text-[10px] font-semibold text-primary uppercase tracking-wider">
                          Today
                        </span>
                      )}
                    </div>
                    <p className="text-[11.5px] text-muted-foreground mt-0.5 truncate">
                      {dayActivities.length === 0
                        ? 'No activities'
                        : `${dayActivities.length} ${dayActivities.length === 1 ? 'activity' : 'activities'}`}
                    </p>
                  </div>
                  <ChevronDown
                    className={cn(
                      'w-5 h-5 text-muted-foreground shrink-0 transition-transform dur-base ease-out-soft',
                      isExpanded && 'rotate-180'
                    )}
                    strokeWidth={2}
                  />
                </button>
                {dayActivities.length > 0 && (
                  <button
                    className="px-3 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-card-2 transition-colors border-l border-border"
                    onClick={(e) => {
                      e.stopPropagation()
                      setSheetDay({ day: dateStr, acts: dayActivities })
                    }}
                    aria-label="Generate day sheet"
                    title="Generate day sheet (PDF)"
                  >
                    <FileText className="w-4 h-4" strokeWidth={2} />
                  </button>
                )}
              </div>

              <div
                className={cn(
                  'grid transition-[grid-template-rows] dur-base ease-out-soft',
                  isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                )}
              >
                <div className="overflow-hidden">
                  <div className="border-t border-border">
                    {dayActivities.length === 0 ? (
                      <p className="px-4 py-3 text-sm text-muted-foreground italic">
                        No activities for this day
                      </p>
                    ) : (
                      <div className="divide-y divide-border">
                        {dayActivities.map(activity => (
                          <ActivityRow
                            key={activity.id}
                            activity={activity}
                            assignedCount={assignmentsByActivity[activity.id]?.length ?? 0}
                            status={statusMap[activity.id]}
                            onEdit={() => openEditActivity(activity)}
                            onDelete={() => handleDelete(activity)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Fixed-footer FAB — solo mobile/tablet. Desktop no lo ve. */}
      <div className="manager-fab-dock lg:hidden">
        <div className="max-w-lg md:max-w-2xl mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <button onClick={openNewActivity} className="manager-fab">
              <Plus className="w-5 h-5" strokeWidth={2.5} />
              Add activity
            </button>
          </div>
        </div>
      </div>

      <ActivityFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        tour={tour}
        editingActivity={editingActivity}
      />

      {sheetDay && (
        <DaySheetDialog
          tour={tour}
          day={sheetDay.day}
          activities={sheetDay.acts}
          open
          onOpenChange={(o) => { if (!o) setSheetDay(null) }}
        />
      )}
    </div>
  )
}

// ── Desktop right panel ─────────────────────────────────────────────────────

interface DayPanelProps {
  tour: Tour
  day: string
  activities: Activity[]
  assignmentsByActivity: Record<string, string[]>
  statusMap: Record<string, ActivityStatus>
  onAdd: () => void
  onDaySheet: () => void
  onEdit: (a: Activity) => void
  onDelete: (a: Activity) => void
}

function DayPanel({
  day, activities, assignmentsByActivity, statusMap, onAdd, onDaySheet, onEdit, onDelete,
}: DayPanelProps) {
  const d = parseISO(day)
  const isToday = day === format(new Date(), 'yyyy-MM-dd')
  const count = activities.length

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden animate-in fade-in dur-base ease-out-soft shadow-[0_1px_2px_0_rgba(0,0,0,0.04)] dark:shadow-[0_1px_2px_0_rgba(0,0,0,0.25)]">
      {/* Panel header: kicker + title + today chip + meta + actions */}
      <header className="flex items-start gap-3 px-5 lg:px-6 py-5 border-b border-border">
        <div className="flex-1 min-w-0">
          <p className="mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--subtle-foreground)] mb-1">
            {format(d, 'MMMM d, yyyy')}
          </p>
          <div className="flex items-baseline gap-2.5 flex-wrap">
            <h2 className="text-[22px] lg:text-[24px] font-semibold tracking-[-0.015em] text-foreground leading-tight">
              {format(d, 'EEEE')}
            </h2>
            {isToday && (
              <span className="text-[10px] font-semibold uppercase tracking-wider text-primary bg-primary/10 rounded px-1.5 py-0.5">
                Today
              </span>
            )}
          </div>
          <p className="text-[12px] text-muted-foreground mt-1.5">
            {count === 0
              ? 'No activities scheduled'
              : `${count} ${count === 1 ? 'activity' : 'activities'}`}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {count > 0 && (
            <button
              type="button"
              onClick={onDaySheet}
              className="icon-btn"
              aria-label="Generate day sheet"
              title="Generate day sheet (PDF)"
            >
              <FileText className="w-4 h-4" strokeWidth={2} />
            </button>
          )}
          <button
            type="button"
            onClick={onAdd}
            className={cn(
              'inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground text-[13px] font-semibold px-3.5 h-9 transition-all dur-base ease-out-soft',
              'shadow-[0_6px_18px_-8px_color-mix(in_oklab,var(--primary)_45%,transparent)]',
              'hover:shadow-[0_10px_24px_-10px_color-mix(in_oklab,var(--primary)_60%,transparent)]',
              'hover:-translate-y-[1px] active:translate-y-0 active:scale-[0.99]',
            )}
            title="Add activity (press N)"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={2.75} />
            Add activity
          </button>
        </div>
      </header>

      {/* Activity list or empty state */}
      {count === 0 ? (
        <DayEmptyState onAdd={onAdd} />
      ) : (
        <div className="divide-y divide-border">
          {activities.map((activity, i) => (
            <div
              key={activity.id}
              style={{ animationDelay: `${i * 30}ms` }}
              className="animate-in fade-in slide-in-from-bottom-1 fill-mode-backwards dur-base ease-out-soft"
            >
              <ActivityRow
                activity={activity}
                assignedCount={assignmentsByActivity[activity.id]?.length ?? 0}
                status={statusMap[activity.id]}
                onEdit={() => onEdit(activity)}
                onDelete={() => onDelete(activity)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function DayEmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      <div className="w-16 h-16 rounded-2xl bg-card-2 flex items-center justify-center mb-4 border border-border">
        <CalendarIcon className="w-7 h-7 text-muted-foreground" strokeWidth={1.75} />
      </div>
      <h3 className="font-semibold text-foreground mb-1">No activities on this day</h3>
      <p className="text-sm text-muted-foreground max-w-xs mb-4 leading-relaxed">
        Pick another day from the list or add one now.
      </p>
      <button
        type="button"
        onClick={onAdd}
        className={cn(
          'inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground text-[13px] font-semibold px-3.5 h-9 transition-all dur-base ease-out-soft',
          'shadow-[0_6px_18px_-8px_color-mix(in_oklab,var(--primary)_45%,transparent)]',
          'hover:shadow-[0_10px_24px_-10px_color-mix(in_oklab,var(--primary)_60%,transparent)]',
          'hover:-translate-y-[1px] active:translate-y-0 active:scale-[0.99]',
        )}
      >
        <Plus className="w-3.5 h-3.5" strokeWidth={2.75} />
        Add activity
      </button>
    </div>
  )
}

// ── Activity row (shared mobile + desktop) ──────────────────────────────────

interface RowProps {
  activity: Activity
  assignedCount: number
  status?: ActivityStatus
  onEdit: () => void
  onDelete: () => void
}

function ActivityRow({ activity, assignedCount, status, onEdit, onDelete }: RowProps) {
  const startTime = formatInTimezone(activity.start_time_utc, activity.venue_timezone)
  const tzAbbr = getTimezoneAbbr(activity.venue_timezone)
  const isPublic = activity.visibility_level === 'public'

  return (
    <div
      role="button"
      onClick={onEdit}
      className="schedule-row cursor-pointer group relative active:scale-[0.995] transition-transform dur-fast"
    >
      {/* Status dot — live (primary pulse) / next (primary) / past (muted).
          'upcoming' y undefined quedan sin dot para no saturar. */}
      {status && status !== 'upcoming' && (
        <span
          className={cn(
            'absolute left-[6px] top-[19px] w-1.5 h-1.5 rounded-full',
            status === 'live' && 'bg-primary animate-pulse',
            status === 'next' && 'bg-primary',
            status === 'past' && 'bg-[color:var(--subtle-foreground)] opacity-35'
          )}
          aria-hidden
          title={status}
        />
      )}

      <div className="sr-time mono">
        <span>{startTime}</span>
        <span className="tz">{tzAbbr}</span>
      </div>

      <div className="min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={cn('vis-chip', isPublic ? 'vis-chip-public' : 'vis-chip-assigned')}>
            {activity.visibility_level}
          </span>
          {!isPublic && (
            <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
              <Users className="w-3 h-3" strokeWidth={2.5} />
              <span className="tabular-nums">{assignedCount}</span>
            </span>
          )}
        </div>
        <p className="sr-title truncate">{titleWithEmoji(activity.titulo)}</p>
        {activity.venue_name && (
          <p className="sr-venue truncate">{activity.venue_name}</p>
        )}
      </div>

      <div className="sr-chev flex items-center gap-0.5">
        <button
          onClick={e => { e.stopPropagation(); onEdit() }}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-muted-foreground hover:text-foreground"
          aria-label="Edit"
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={e => { e.stopPropagation(); onDelete() }}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-muted-foreground hover:text-destructive"
          aria-label="Delete"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
        <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
      </div>
    </div>
  )
}
